import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Gym } from '../types';
import { searchGymsByLocation, resolveLocation } from '../services/locationService';
import { calculateHaversineDistance } from '../utils/geo';
import { safeErrorMessage } from '../utils/sanitize';
import { toDisplaySuburb } from '../lib/slugs';
import { suburbSlugFromPathParts } from '../lib/stateRoutes';
import type { StateSlug } from '../lib/stateCore.mjs';

/**
 * UI-49 — the two backstops below were ONE timer, and that was the defect.
 *
 * (a) MAP_IDLE_BACKSTOP_MS releases the map blur when GoogleMapView never reports
 *     itself idle (see GoogleMapView's onIdle note). That is a MAP concern.
 * (b) SEARCH_SETTLE_BACKSTOP_MS is a last resort for a request that never resolves
 *     at all. That is a SEARCH concern.
 *
 * A single 6s timer used to do both: it declared the search finished without ever
 * asking whether an answer had arrived, so any search slower than 6s painted a
 * false "no gyms found" and snapped the map shut until the real results landed.
 * Settling is now driven by the request lifecycle — performSearch's `finally` and
 * the reveal-settle effect — both of which know whether results actually arrived.
 *
 * COVERS: the auto-search effect's own request. DOES NOT COVER: `quiet` reconcile
 * searches, which deliberately never touch the reveal flags.
 */
const MAP_IDLE_BACKSTOP_MS = 6000;
const SEARCH_SETTLE_BACKSTOP_MS = 20000;

interface UseDirectorySearchOptions {
    filters: {
        suburb: string;
        radius: number;
        useRadius: boolean;
        location_entry_id?: string | null;
        location_source?: "index" | "legacy" | null;
    };
    currentSearchKey: string;
    cachedResults?: Gym[] | undefined;
    cachedSearchLocation: string;
    setCachedSearchLocation: (location: string) => void;
    cachedSearchCenter?: { lat: number; lng: number } | null | undefined;
    setCachedSearchCenter?: ((center: { lat: number; lng: number } | null) => void) | undefined;
    isVisible?: boolean | undefined;
    gymName?: string;
    /** Pre-fetched gyms injected by the SSG build for zero-latency first paint. */
    ssGInitialGyms?: Gym[] | undefined;
    /**
     * UI-131 PART D — the suburb's centre, baked by the SSG build.
     *
     * ⚠️ THIS IS NOW THE ONLY SOURCE ON A COLD ARRIVAL. The mount reconcile that used to
     * supply it is gone (see the DL-1 note on that effect). Without it `useDirectoryResults`
     * falls back to the STATE centre and ranks a suburb's gyms by their distance from the
     * middle of the state. The build refuses to emit a directory route without one.
     */
    ssgSearchCenter?: { lat: number; lng: number } | null | undefined;
    /**
     * R-02: the state the current route is scoped to (from useRouteStateSlug).
     * Every state-capable callable used to default to NSW when this was omitted,
     * so a VIC page that failed to pass it searched the NSW corpus. UI-01
     * hardening: REQUIRED — omitting the state fails the build; a state-less
     * surface must pass DEFAULT_STATE deliberately (useRouteStateSlug already
     * resolves state-less routes to DEFAULT_STATE).
     */
    stateSlug: StateSlug;
}

export function useDirectorySearch({
    filters,
    currentSearchKey,
    setCachedSearchLocation,
    setCachedSearchCenter,
    isVisible = true,
    gymName,
    ssGInitialGyms,
    ssgSearchCenter,
    stateSlug
}: UseDirectorySearchOptions) {
    const hasSSGData = !!(ssGInitialGyms?.length);
    const [allGyms, setAllGyms] = useState<Gym[]>(ssGInitialGyms ?? []);
    const [displayReady, setDisplayReady] = useState(hasSSGData);
    // UI-131 Part D: SEEDED from the build, not null. Every later writer is unchanged —
    // a user search still overwrites it via performSearch, and the three reset paths still
    // clear it. This only changes where a COLD ARRIVAL's value comes from: the payload
    // instead of a background round-trip.
    const [searchCenter, setSearchCenter] = useState<{ lat: number; lng: number } | null>(ssgSearchCenter ?? null);
    const [isAutoSearching, setIsAutoSearching] = useState(false);
    const [isMapMoving, setIsMapMoving] = useState(false);
    // The backend resolves the searched location to its canonical "Suburb Postcode"
    // (e.g. "Alexandria 2015") and returns it; exposed for display so a Google-entry
    // page — whose URL slug intentionally omits the postcode — can still show it.
    const [resolvedLocation, setResolvedLocation] = useState<string>('');
    // NOTE: despite the legacy name, this does NOT track real image loads. It is
    // flipped true by the reveal-settle effect below (a double requestAnimationFrame
    // after fresh gyms arrive), so the reveal-gating effect lifts the blur/skeleton
    // only once the newly-mounted cards have painted a frame.
    const [hydrationDelayComplete, setHydrationDelayComplete] = useState(false);
    const [isRevealed, setIsRevealed] = useState(hasSSGData);

    // ── Item 2 (operator-ruled 2026-08-14): the cross-state PAINT window ──────────
    // Results belong to a STATE. Until this existed, a cross-state navigation
    // changed the heading, title and canonical synchronously (they derive from the
    // pathname) while `allGyms` still held the OUTGOING state's gyms — so a visitor
    // could see Victorian gyms sitting under a "NSW" heading until the new search
    // returned.
    //
    // ⚠️ THE FIX THAT WAS ORIGINALLY PRESCRIBED FOR THIS DOES NOTHING, and is
    // recorded here so nobody re-attempts it: the row proposed hardening against
    // STALE ANSWERS, but all three result paths already reject them via
    // `searchRequestIdRef`. Stale answers were never the cause. The cause is that
    // nothing cleared the results AT THE FLIP — only at the next arrival.
    //
    // ⚠️ WHY THIS IS A RENDER-PHASE CLEAR AND NOT A useEffect: an effect runs AFTER
    // the browser has painted, so the wrong-state frame would still be shown — the
    // exact defect — merely for one frame less. Setting state during render of the
    // same component is React's supported way to derive state from a changed input;
    // React discards the in-progress render and re-runs before painting anything.
    // Do NOT "tidy" this into a useEffect or a useLayoutEffect (the latter also
    // warns during SSG prerendering, which every one of these 4,202 pages goes
    // through).
    //
    // Clears exactly the two things that can PAINT the outgoing state, and nothing
    // more: `allGyms` (the result list and the map pins) and `resolvedLocation`
    // (the "Bondi 2026" location pill). `filters`/`searchCenter` are deliberately
    // NOT touched — filter travel is ruling (ii)'s and lives in the URL builders.
    //
    // ⚠️ `setIsRevealed(false)` / `setDisplayReady(false)` were HERE and were
    // REMOVED, because a mutation proved them redundant: dropping both while
    // keeping these two left every test in
    // `useDirectorySearch.crossStatePaint.test.tsx` green, including the one that
    // asserts the hook reports the searching state during the window. The flip
    // commit's own search path already clears those flags. The behaviour is still
    // guarded — that test fails if anything ever stops clearing them — so the
    // guarantee is kept without carrying two lines nothing justifies. Do not
    // re-add them "for safety" without a test that fails when they are absent.
    const [resultsStateSlug, setResultsStateSlug] = useState<StateSlug>(stateSlug);
    if (resultsStateSlug !== stateSlug) {
        setResultsStateSlug(stateSlug);
        setAllGyms([]);
        setResolvedLocation('');
    }

    const searchRequestIdRef = useRef<number>(0);
    const lastSearchedLocationRef = useRef<string>('');
    const mapMovingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // UI-49: retired the moment a real answer arrives (performSearch's `finally`),
    // so reaching its callback means no answer came at all.
    const settleBackstopRef = useRef<NodeJS.Timeout | null>(null);
    const didHydrateReconcileRef = useRef(false);
    // Hold-fire (changelog (+11)): the state the previous effect RUN observed, so a
    // run can tell it is the first one after a cross-state flip. NOT the naïve
    // ref-read pattern from FilterModal/HeaderSearch — dropping stateSlug from the
    // deps here would stop the Epping class (19 same-name cross-state pairs, whose
    // filters never change across the navigation) from ever re-searching, which is
    // exactly the R-02 bug the stateQualifiedKey exists to prevent.
    const prevRunStateRef = useRef<StateSlug | null>(null);
    const routerLocation = useLocation();
    // The guard reads the pathname through a render-updated ref, NOT an effect
    // dep: a pathname dep re-runs the effect on style-only navigations, and each
    // re-run first executes the PREVIOUS run's cleanup (cancelled = true) — which
    // could kill an in-flight resolveLocation-path search mid-await and strand
    // the page on the old results (review RISK). Flip runs are already triggered
    // by the stateSlug dep, so the dep buys nothing the ref doesn't.
    const pathnameRef = useRef(routerLocation.pathname);
    pathnameRef.current = routerLocation.pathname;
    const routerStateFiltersRef = useRef<boolean>(false);
    routerStateFiltersRef.current = !!(routerLocation.state as { filters?: unknown } | null)?.filters;

    // Helper: Perform the actual search
    const performSearch = useCallback(async (location: string, radius: number, postcodeOnly: boolean, entryId?: string | null, opts?: { quiet?: boolean }) => {
        // A `quiet` search fetches + swaps the result set WITHOUT flipping the
        // reveal flags (isAutoSearching / display state), so it can reconcile the
        // instant SSG paint in the background with no skeleton flash.
        const quiet = opts?.quiet === true;
        const requestId = ++searchRequestIdRef.current;
        if (!quiet) setIsAutoSearching(true);

        try {
            const result = await searchGymsByLocation(location, radius, postcodeOnly, { entryId, state: stateSlug });

            // Guard against stale requests and component unmounts
            if (requestId !== searchRequestIdRef.current) return;

            // Surface the backend's canonical "Suburb Postcode" for display; consumers
            // guard against staleness (only used when it matches the current suburb).
            if (result.resolved) setResolvedLocation(result.resolved);

            if (result.center) {
                setSearchCenter(result.center);
                if (setCachedSearchCenter) {
                    setCachedSearchCenter(result.center);
                }
            }

            if (result.gyms && result.gyms.length > 0) {
                let processedGyms = result.gyms;
                if (radius > 0 && result.center && !postcodeOnly) {
                    processedGyms = result.gyms.filter(gym => {
                        if (Number.isFinite(gym.lat) && Number.isFinite(gym.lng)) {
                            const dist = calculateHaversineDistance(result.center!.lat, result.center!.lng, gym.lat, gym.lng);
                            return dist <= radius;
                        }
                        return false;
                    });
                }
                if (!quiet) setDisplayReady(false);
                setAllGyms(processedGyms);
            } else if (!quiet) {
                // Quiet reconcile treats an empty result as inconclusive and keeps
                // the instant SSG gyms rather than blanking the page.
                setAllGyms([]);
            }
        } catch (error) {
            console.error("[useDirectorySearch] Search failed:", safeErrorMessage(error));
        } finally {
            if (requestId === searchRequestIdRef.current && !quiet) {
                setIsAutoSearching(false);
                // UI-49: a real answer arrived (or failed) for the CURRENT request,
                // so the last-resort settle backstop must not fire later against a
                // state it no longer describes.
                if (settleBackstopRef.current) {
                    clearTimeout(settleBackstopRef.current);
                    settleBackstopRef.current = null;
                }
            }
        }
    }, [setCachedSearchCenter, stateSlug]);

    // DL-1: On SSG hydration, adopt the pre-rendered gyms as the current result
    // set instead of discarding them for a skeleton + refetch. Seed the search
    // guard so the main effect below skips its reset, then quietly reconcile with
    // the authoritative full result set (the SSG payload is only the first page
    // and carries no search center for distance/radius sorting).
    // R-02 review fix: the dedupe key MUST carry the state. getCanonicalSearchKey is
    // suburb+radius only, and toDisplaySuburb strips the URL postcode, so
    // /nsw/epping-2121/gyms and /vic/epping-3076/gyms collapse to the same key —
    // 19 cross-state suburb-name pairs exist. Without the prefix, a cross-state
    // SPA navigation between them hits the searchKey guard below, skips the
    // re-search, and keeps the OTHER state's results under this state's metadata.
    const stateQualifiedKey = (key: string) => `${stateSlug}|${key}`;

    // ⚰️ UI-131 PART D (operator-ruled 2026-08-26) — THE QUIET RECONCILE SEARCH IS GONE.
    // What remains is the GUARD-SEEDING, and removing it too would be a regression, not a
    // simplification: without these three lines `lastSearchedLocationRef` never matches
    // this route's key, so the main effect below falls through to "Case 3: Standard
    // location search" and fires a FULL, non-quiet search — skeletons on every cold
    // arrival. The network call was the optional half; the guard was never optional.
    //
    // WHY THE SEARCH WENT. It re-fetched, per prerendered directory view, a result set the
    // page already had. Its three outputs were measured before removal: `searchCenter` (now
    // baked — `ssgSearchCenter` above, the reason Part D exists), `resolvedLocation`
    // (already superseded — `resolveLocationLabel` PREFERS the baked postcode on a
    // prerendered page), and `allGyms` (the payload carries every gym in the suburb, not a
    // slice; `lib/ssGData.ts` says so in its own docblock).
    //
    // ⚠️ DL-1 REQUIRED THIS SEARCH — `docs/audit/directory-loading-ux-audit.md`, deployed
    // 2026-07-03: "Do NOT do the bare seed — must reconcile the SSG 12-gym slice vs the
    // full callable result + searchCenter." BOTH of its reasons are discharged, which is
    // why this is allowed to go: the 12-gym slice no longer exists (measured — Brookvale
    // bakes 20 of 20, no cap anywhere in the SSG path), and `searchCenter` is baked. **If
    // either ever stops being true, DL-1 applies again and this search must come back.**
    //
    // ⚠️ ONE BEHAVIOUR DELIBERATELY CHANGED, not overlooked: the callable applied a 10 km
    // radius filter the build does not, so it silently dropped gyms sitting far from their
    // own suburb centre. Measured across all 988 suburb routes it dropped exactly TWO, both
    // bad records — a gym geocoded 1,853 km away and one filed under the wrong suburb. Both
    // were corrected in production the same day (`docs/audit/nsw-gym-location-corrections-2026-08-26.json`).
    // **The filter was masking data defects rather than doing a job; it is not reinstated.**
    useEffect(() => {
        if (didHydrateReconcileRef.current) return;
        if (!hasSSGData || !filters.suburb) return;
        didHydrateReconcileRef.current = true;
        lastSearchedLocationRef.current = stateQualifiedKey(currentSearchKey);
        setCachedSearchLocation(stateQualifiedKey(currentSearchKey));
        // Mount-only: intentionally runs once for the initial SSG payload.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect: Trigger search when filters or visibility change
    useEffect(() => {
        if (!isVisible) return;

        const stateFlipped = prevRunStateRef.current !== null && prevRunStateRef.current !== stateSlug;
        prevRunStateRef.current = stateSlug;

        const { suburb } = filters;

        // Case 1: Search by name only (Global search)
        if (!suburb && gymName && gymName.trim().length >= 3) {
            // Same review fix: the name tag is state-qualified too, so a persisting
            // gymName filter re-searches after a cross-state navigation.
            const searchTag = stateQualifiedKey(`name_${gymName}`);
            if (lastSearchedLocationRef.current === searchTag && (allGyms.length > 0 || isAutoSearching)) {
                return;
            }

            lastSearchedLocationRef.current = searchTag;
            setAllGyms([]);
            setSearchCenter(null);
            setIsAutoSearching(true);
            setDisplayReady(false);

            // Perform name-prefix-contains search
            const performNameSearch = async () => {
                try {
                    const { searchGymsByName } = await import('../services/locationService');
                    const results = await searchGymsByName(gymName, stateSlug);
                    if (lastSearchedLocationRef.current === searchTag) {
                        setAllGyms(results);
                        setDisplayReady(true);
                        setIsRevealed(true);
                        setHydrationDelayComplete(true);
                    }
                } catch (error) {
                    console.error("[useDirectorySearch] Name search failed:", safeErrorMessage(error));
                } finally {
                    setIsAutoSearching(false);
                }
            };
            performNameSearch();
            return;
        }

        // Case 2: No search criteria
        if (!suburb) {
            if (lastSearchedLocationRef.current !== '') {
                setAllGyms([]);
                setSearchCenter(null);
                lastSearchedLocationRef.current = '';
                setIsRevealed(false);
                setDisplayReady(false);
            }
            return;
        }

        const searchKey = stateQualifiedKey(currentSearchKey);
        if (searchKey === lastSearchedLocationRef.current) {
            // If we have results, ensure they are visible even if we skip the search
            if (allGyms.length > 0) {
                setDisplayReady(true);
            }
            return;
        }

        // HOLD FIRE on the flip commit of a cross-state navigation (changelog (+11)).
        // useRouteStateSlug derives stateSlug synchronously from the pathname, but
        // useDirectoryFilters syncs `filters` from the URL in an effect — one commit
        // later. The first run after a state flip therefore sees the NEW state paired
        // with the OUTGOING page's filters, and used to dispatch a search callable
        // whose result the requestId guard then threw away (one wasted invocation per
        // cross-state hop). Discriminator: derive the pathname's own suburb with the
        // SAME recipe useDirectoryFilters uses — if filters.suburb disagrees, a
        // settle run follows (filters change to match this pathname), so skipping is
        // safe; if it agrees (the Epping class — same-name pair, filters never
        // change), this IS the correct search and fires now.
        // EXCEPTION (review RISK): a navigation carrying router state.filters is
        // exempt from useDirectoryFilters' URL sync (effect #4 defers to state.filters
        // and effect #5 dedupes re-applies), so for THOSE the settle run is NOT
        // guaranteed — holding fire there could suppress the search forever. When
        // state.filters rides the navigation, fall through and fire (at worst the
        // pre-change wasted-call behaviour, never a dead page).
        // Scoped to stateFlipped runs only, so in-app filter changes that precede
        // their URL sync (modal apply, radius) behave exactly as before. The
        // discriminator compares SUBURBS only: a same-name cross-state hop whose
        // URL carries a different ?radius still fires one superseded call —
        // accepted residual, identical to pre-change behaviour for that sub-case.
        if (stateFlipped && !routerStateFiltersRef.current) {
            const pathParts = pathnameRef.current.split('/');
            const suburbSlug = suburbSlugFromPathParts(pathParts); // shared shape-aware parse (UI-57)
            const pathSuburb = suburbSlug ? toDisplaySuburb(suburbSlug) : '';
            if (pathSuburb !== suburb) {
                // Stale-filters window: do NOT record this key or dispatch — the
                // settle run does both with the real pair.
                return;
            }
        }

        // Case 3: Standard location search
        lastSearchedLocationRef.current = searchKey;
        setCachedSearchLocation(searchKey);

        let cancelled = false;

        const executeSearch = async () => {
            // Resolve entryId if missing (backwards-compat for slugs). Only split
            // when the suburb ends in a real 4-digit postcode (e.g. "Prestons 2170");
            // otherwise leave entryId null and let the backend resolve the raw
            // location string — avoids mis-parsing multi-word suburbs like
            // "North Sydney" into suburb + a bogus "postcode".
            let entryId = filters.location_entry_id;
            if (!entryId && filters.suburb) {
                const parts = filters.suburb.trim().split(' ').filter(Boolean);
                const maybePostcode = parts[parts.length - 1];
                if (parts.length > 1 && maybePostcode && /^[0-9]{4}$/.test(maybePostcode)) {
                    const suburb = parts.slice(0, -1).join(' ');
                    // R-02: was hardcoded DEFAULT_STATE_DISPLAY ('NSW'); the server
                    // uppercases, so the slug form resolves identically for any state.
                    const resolved = await resolveLocation({ state: stateSlug, suburb, postcode: maybePostcode });
                    if (cancelled) return; // superseded by a newer search during the await
                    if (resolved?.entryId) {
                        entryId = resolved.entryId;
                    }
                }
            }

            if (cancelled) return; // superseded before the search even started

            // Reset state for new search
            setAllGyms([]);
            setSearchCenter(null);
            setResolvedLocation(''); // clear the previous suburb's postcode label
            setIsAutoSearching(true);
            setDisplayReady(false);
            setIsRevealed(false);
            setHydrationDelayComplete(false);
            setIsMapMoving(true);

            // UI-49: map backstop ONLY — this no longer declares the SEARCH settled.
            if (mapMovingTimeoutRef.current) clearTimeout(mapMovingTimeoutRef.current);
            mapMovingTimeoutRef.current = setTimeout(() => {
                setIsMapMoving(false);
            }, MAP_IDLE_BACKSTOP_MS);

            // UI-49: last resort for a request that never resolves. A permanent
            // skeleton is worse than a wrong empty state, so this still force-settles
            // — but only after the request has had far longer than the map backstop,
            // and it is cleared as soon as any real answer arrives.
            if (settleBackstopRef.current) clearTimeout(settleBackstopRef.current);
            settleBackstopRef.current = setTimeout(() => {
                setIsMapMoving(false);
                setIsAutoSearching(false);
                setDisplayReady(true);
            }, SEARCH_SETTLE_BACKSTOP_MS);

            performSearch(filters.suburb, filters.radius, !filters.useRadius, entryId);
        };

        executeSearch();

        return () => {
            cancelled = true;
            // DL-3: also clear the fallback timers so a leftover from this search
            // can't fire against later state (previously it was cleared only at the
            // start of the NEXT executeSearch, never on unmount / non-search re-run).
            // UI-49: BOTH timers must be cleared here — the settle backstop inherits
            // DL-3's hazard exactly, and it outlives the map one by 14 seconds.
            if (mapMovingTimeoutRef.current) {
                clearTimeout(mapMovingTimeoutRef.current);
                mapMovingTimeoutRef.current = null;
            }
            if (settleBackstopRef.current) {
                clearTimeout(settleBackstopRef.current);
                settleBackstopRef.current = null;
            }
        };
    }, [
        isVisible,
        filters.suburb,
        filters.radius,
        filters.useRadius,
        gymName,
        currentSearchKey,
        performSearch,
        setCachedSearchLocation,
        setCachedSearchCenter,
        stateSlug
        // routerLocation.pathname is deliberately NOT a dep — the hold-fire guard
        // reads it via pathnameRef (see its declaration for the cancellation-class
        // reasoning); flip runs are already triggered by the stateSlug dep.
    ]);

    // Reveal logic
    useEffect(() => {
        const canReveal = !isAutoSearching && !isMapMoving && hydrationDelayComplete && displayReady;
        if (canReveal && !isRevealed) {
            const timer = setTimeout(() => setIsRevealed(true), 150);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isAutoSearching, isMapMoving, hydrationDelayComplete, displayReady, isRevealed]);

    // Reveal-settle: when a fresh batch of gyms arrives, wait just long enough for
    // the freshly-mounted cards to paint one frame before lifting the blur/skeleton.
    // With no results there is nothing to wait for, so the gate opens immediately.
    useEffect(() => {
        if (allGyms.length === 0) {
            if (!isAutoSearching && !isMapMoving) {
                setDisplayReady(true);
                setHydrationDelayComplete(true);
            }
            return;
        }

        setDisplayReady(false);
        setHydrationDelayComplete(false);

        // DL-2: reveal on the second animation frame (after the browser has painted
        // the mounted cards once) instead of a fixed 2000ms. The sorted data is
        // already final and card images are lazy, so there is nothing to wait 2s for.
        //
        // ⚠️ UI-127 — this comment used to end "; the card grid's own fade-in masks any
        // partial first paint." THAT WAS NEVER TRUE: the grid's fade needed an animation
        // plugin this project does not install, and its element also carried
        // `display: contents`, so it generated no box and could not have faded either way.
        // The dead wrapper is now removed (spec RULED E3). ⚠️ Only the CLAIM is corrected
        // here — the two-frame reveal itself is UNCHANGED and still correct on its own
        // terms (the paint, not the fade, is what it waits for). Do not "restore" a fade
        // to justify this comment; there was never one to restore.
        if (typeof requestAnimationFrame === 'undefined') {
            setDisplayReady(true);
            setHydrationDelayComplete(true);
            return;
        }
        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                setDisplayReady(true);
                setHydrationDelayComplete(true);
            });
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
    }, [allGyms, isAutoSearching, isMapMoving]);

    return {
        allGyms,
        displayReady,
        searchCenter,
        isAutoSearching,
        isMapMoving,
        resolvedLocation,
        // ⚠️ RENAMED FROM `imagesLoaded` / `setImagesLoaded` (UI-126, spec RULED E2).
        // The old names claimed this gate waits for images. IT NEVER DID — it is the
        // hydration delay, and the reveal must NOT wait for photos: measured on the
        // deployed page, DOM-ready is 418ms while the last of the four eager photos
        // lands at 2,801ms, so waiting would be ~7x slower (and that is warm-cache,
        // i.e. a FLOOR). The misleading name is why no instrument in the loading
        // audit found this — the site appeared to already do what a reader assumed.
        //
        // ⚠️ DO NOT re-alias these to an image-shaped name. If you ever want a real
        // "images have loaded" signal, add a SEPARATE one; do not rename this back.
        hydrationDelayComplete,
        isRevealed,
        setIsMapMoving,
        setHydrationDelayComplete
    };
}
